import * as React from "react";

export function formatOiDateTime(value?: string | null): string {
    if (!value) return "Not set";
    let text = value.includes(" ") ? value.replace(" ", "T") : value;
    if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += "Z";
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

type OiHeaderButtonVariant = "primary" | "secondary" | "success" | "danger";

const variantClasses: Record<OiHeaderButtonVariant, string> = {
    primary: "bg-oi-button-blue text-primary-light",
    secondary: "bg-primary-dark dark:bg-primary-dark text-primary-light",
    success: "bg-good-green text-primary-light",
    danger: "bg-bad-red text-primary-light",
};

interface OiHeaderButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: OiHeaderButtonVariant;
    icon?: React.ReactNode;
}

export const OiHeaderButton = React.forwardRef<HTMLButtonElement, OiHeaderButtonProps>(
    function OiHeaderButton({ variant = "primary", icon, className = "", children, type = "button", ...rest }, ref) {
        return (
            <button
                ref={ref}
                type={type}
                {...rest}
                className={`inline-flex h-9 min-w-[6rem] items-center justify-center gap-1.5 whitespace-nowrap border-none px-3 text-sm disabled:opacity-50 ${variantClasses[variant]} ${className}`}
            >
                {icon}
                {children}
            </button>
        );
    }
);

interface OiPageHeaderProps {
    icon: React.ReactNode;
    title: string;
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
    actions?: React.ReactNode;
    steps?: React.ReactNode;
}

export default function OiPageHeader({ icon, title, id, createdAt, updatedAt, actions, steps }: OiPageHeaderProps) {
    const meta = "text-oi-button-blue dark:text-oi-powder-form-yellow";
    const headerRef = React.useRef<HTMLDivElement>(null);
    const [isStuck, setIsStuck] = React.useState(false);

    React.useEffect(() => {
        const update = () => {
            const el = headerRef.current;
            if (!el) return;
            const stickyTop = parseFloat(getComputedStyle(el).top) || 0;
            setIsStuck(el.getBoundingClientRect().top <= stickyTop + 0.5 && window.scrollY > 0);
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, []);

    return (
        <div ref={headerRef} className={`sticky top-[var(--web-nav-offset,0px)] z-[15] -mx-10 -mt-4 mb-3 px-10 pb-2 pt-2 text-primary-dark dark:text-primary-light ${isStuck ? "bg-primary-light dark:bg-oi-dark-background" : "bg-transparent"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-lg font-bold leading-none">
                        {icon}
                        <span>{title}{id ? ":" : ""}</span>
                        {id ? <span className={`${meta} truncate`}>{id}</span> : null}
                    </div>
                    {createdAt ? <div className={`mt-1 text-xs ${meta}`}>Created: {formatOiDateTime(createdAt)}</div> : null}
                    {updatedAt ? <div className={`mt-1 text-xs ${meta}`}>Last update: {formatOiDateTime(updatedAt)}</div> : null}
                </div>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>
            </div>
            {steps ? <div className="mt-2 min-w-0 overflow-x-auto"><div className="min-w-[48rem]">{steps}</div></div> : null}
        </div>
    );
}
